import { useEffect, useState } from 'react';
import { isNumber } from 'lodash';
import { FormField } from 'ui/formField';
import { TypeaheadSelect } from 'ui/selects';
import { PushContentDefinition } from 'modules/graphql/types';
import { LabelWithLink } from 'modules/templateEditor/common';
import { RestrictionBadge } from '../RestrictionBadge';
import { SettingsReadOnlyText } from '../../common/EditorContentContainer.styled';
import { getIsEditMode } from '../../common/utils';
import { RELEVANCE_SCORE_OPTIONS, SETTINGS_DISPLAY_CONTENT } from '../constants';
import { useEditableFieldsContext } from '../../context/EditableFieldsContext';
import { useQueryParamContext } from '../../context/QueryParamContext';
import { StyledTooltip } from '../styles';

/*
  A component used to select the relevance score
  for Push templates.
*/
export const RelevanceScore = () => {
  const id = 'push-settings-select-relevance-score';
  const [hasError, setHasError] = useState(false);
  const { view } = useQueryParamContext();

  const { isEditMode, liveTemplate, setCurrentMediumField } = useEditableFieldsContext();
  const pushData = (liveTemplate?.mediumData || {}) as PushContentDefinition;
  const isEditModeVisible = getIsEditMode(view, isEditMode);

  const getReadOnlyText = () => {
    if (pushData.isSilentPush) return SETTINGS_DISPLAY_CONTENT.deliverSilently.silenced;
    return pushData.relevanceScore?.toString() || SETTINGS_DISPLAY_CONTENT.unsetText;
  };

  const validateEntry = (values: string[]) => {
    const formatted = parseFloat(values[0]);
    const rounded = formatted.toFixed(1);
    const rule = new RegExp(/^(0(\.\d+)?|1(\.0+)?)$/);
    const isValid = rule.test(rounded);

    if (isValid) {
      setHasError(false);
      setCurrentMediumField('relevanceScore', parseFloat(rounded));
    } else {
      setHasError(true);
    }
  };

  useEffect(() => {
    if (!isEditModeVisible) {
      setHasError(false);
    }
  }, [isEditModeVisible]);

  return (
    <FormField
      isDisabled={pushData.isSilentPush}
      helpText={
        isEditModeVisible ? (
          <LabelWithLink
            text={SETTINGS_DISPLAY_CONTENT.relevanceScore.helpText}
            url={SETTINGS_DISPLAY_CONTENT.relevanceScore.helpLink.link}
            openInNewTab
          />
        ) : (
          ''
        )
      }
      hasError={hasError}
      errorText={SETTINGS_DISPLAY_CONTENT.relevanceScore.placeholder}
      isHorizontalLayout
      labelText={SETTINGS_DISPLAY_CONTENT.relevanceScore.labelText}
      restrictionText={
        <RestrictionBadge
          text={SETTINGS_DISPLAY_CONTENT.relevanceScore.restriction}
          hasTooltip={true}
          tooltipText={SETTINGS_DISPLAY_CONTENT.iOSTooltip}
        />
      }
    >
      {isEditModeVisible ? (
        <StyledTooltip
          isEnabled={pushData.isSilentPush}
          tooltipContent={SETTINGS_DISPLAY_CONTENT.deliverSilently.restriction}
        >
          <TypeaheadSelect
            isDisabled={pushData.isSilentPush}
            onSelect={validateEntry}
            defaultValues={
              isNumber(pushData.relevanceScore) ? [pushData.relevanceScore.toString()] : []
            }
            placeholder={SETTINGS_DISPLAY_CONTENT.relevanceScore.placeholder}
            id={id}
            title={id}
            data-test={id}
            options={RELEVANCE_SCORE_OPTIONS}
            style={{ width: '100%' }}
            isError={hasError}
            isFullWidth
          />
        </StyledTooltip>
      ) : (
        <SettingsReadOnlyText data-test="relevance-score-ro" isUnset={!pushData.relevanceScore}>
          {getReadOnlyText()}
        </SettingsReadOnlyText>
      )}
    </FormField>
  );
};
